import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Paginacao } from "app/components/itens-orcamento/paginacao/Paginacao";
import { classeParaLabelsFormularioComFundoEscuroSimplificada } from "app/estilos/classeParaLabelsFormularioComFundoEscuro";
import { DocumentoQuitado } from "app/types/cliente/RespostaDocumentosQuitados";
import { IColuna } from "app/types/IColuna";
import clsx from "clsx";
import globalMU from "globalMU";
import indexOf from "lodash/indexOf";
import sortBy from "lodash/sortBy";
import React, { useMemo } from "react";
import Legenda from "../../itens-orcamento/legenda/Legenda";
import { AcoesHeadDocumentosQuitados } from "./table-head/AcoesHeadDocumentosQuitados";

const estiloTabela = require('../../../estilos/tabelas.module.scss').default
const estilosFormulario = require('../../../estilos/formulario.module.scss').default
import { fonteGradeOuHerdada } from 'app/utils/tamanhoFonteGrid'
const nomeColunaFonteReceita = "Fonte de Receita";

const colunasBase = [
    "Número",
    "Emissão",
    "Vencimento",
    "Recebimento",
    "Tipo de Documento",
    "Dias Atraso",
    "Valor",
    "Baixa Parcial",
    "Encargos",
    "Descontos",
    "Total",
    "Descrição",
    "Histórico",
]

const colunasAlinhadasDireita = ["Dias Atraso", "Valor", "Baixa Parcial", "Encargos", "Descontos", "Total"]
const colunasSemIconeOrdenacao = ["Tipo de Documento", "Histórico", "Total", "Descontos", "Encargos", "Baixa Parcial"]

// mapa estatico: evita reconstruir o R.cond (14 closures) a cada celula renderizada
const conteudoPorColuna: { [nomeColuna: string]: (item: DocumentoQuitado) => React.ReactNode } = {
    "Número": (item) => item.Numero,
    "Emissão": (item) => globalMU.formatDate(item.Emissao),
    "Vencimento": (item) => globalMU.formatDate(item.Vencimento),
    "Recebimento": (item) => globalMU.formatDate(item.Pagamento),
    "Tipo de Documento": (item) => item.TipoDocumentoRecebimento,
    "Dias Atraso": (item) => item.DiasAtraso,
    "Valor": (item) => globalMU.formatarValorNumerico(item.ValorNaMoeda1, 2, false),
    "Baixa Parcial": (item) => globalMU.formatarValorNumerico(item.BaixaParcial, 2, false),
    "Encargos": (item) => globalMU.formatarValorNumerico(item.Encargos, 2, false),
    "Descontos": (item) => globalMU.formatarValorNumerico(item.Descontos, 2, false),
    "Total": (item) => globalMU.formatarValorNumerico(item.Total, 2, false),
    "Descrição": (item) => item.Descricao,
    "Histórico": (item) => item.Observacao,
    [nomeColunaFonteReceita]: (item) => item.FonteDeReceita, // 487678/449093
}

type CelulaDocumentoQuitadoProps = {
    nomeColuna: string
    item: DocumentoQuitado
}

const CelulaDocumentoQuitado = React.memo(({ nomeColuna, item }: CelulaDocumentoQuitadoProps) => {
    const obterConteudo = conteudoPorColuna[nomeColuna]
    if (obterConteudo == null) {
        return null
    }

    return (
        <td
            id={`documentosQuitadosCliente_celula_${nomeColuna}_${item.OidContaReceber}`}
            className={indexOf(colunasAlinhadasDireita, nomeColuna) !== -1 ? estiloTabela.dadoNumerico : undefined}
            style={nomeColuna === "Número"
                ? { whiteSpace: "nowrap", boxShadow: item.LucrosEPerdas ? "4px 0 #00c800 inset" : " " }
                : undefined}
        >
            {obterConteudo(item)}
        </td>
    )
})

type CabecalhoColunaDocumentoQuitadoProps = {
    nomeColuna: string
    colunaOrdenacao: string
    ordemOrdenacaoDecrescente: boolean
    onAlterarOrdenacaoDados: (coluna: string, decrescente: boolean) => void
}

const CabecalhoColunaDocumentoQuitado = React.memo((props: CabecalhoColunaDocumentoQuitadoProps) => {
    const ordenadaPorEstaColuna = props.colunaOrdenacao == props.nomeColuna

    // coluna nao ordenada mostra o UnfoldMore (as duas setas), indicador padrao do
    // Material-UI para "coluna ordenavel", que nao sugere uma direcao que nao esta ativa
    const IconeOrdenacao = !ordenadaPorEstaColuna
        ? UnfoldMoreIcon
        : props.ordemOrdenacaoDecrescente ? ArrowUpwardIcon : ArrowDownwardIcon

    return (
        <th
            id={`documentosQuitadosCliente_cabecalho_${props.nomeColuna}`}
            className={indexOf(colunasAlinhadasDireita, props.nomeColuna) !== -1 ? estiloTabela.cabecalhoNumerico : estiloTabela.cabecalhoTexto}
        >
            <div className="_flex _justify-between _items-center _gap-x-2">
                {props.nomeColuna}
                {indexOf(colunasSemIconeOrdenacao, props.nomeColuna) === -1 && (
                    <div
                        className="_flex _items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => props.onAlterarOrdenacaoDados(props.nomeColuna, ordenadaPorEstaColuna ? !props.ordemOrdenacaoDecrescente : false)}
                    >
                        <IconeOrdenacao
                            style={{
                                fontSize: "1rem",
                                // coluna ordenada fica verde; as demais herdam a cor do cabecalho
                                // com opacidade reduzida, para ficar legivel nos dois temas
                                color: ordenadaPorEstaColuna ? "var(--mu-cor-ordenacao-ativa, #008000)" : "currentColor",
                                opacity: ordenadaPorEstaColuna ? 1 : 0.45,
                            }}
                        />
                    </div>
                )}
            </div>
        </th>
    )
})

export type DocumentoQuitadoProps = {
    entidades: DocumentoQuitado[];
    valorTotalDocumentosQuitados: number;
    cpfCnpj: string;
    nome: string;
    numeroTotalPaginas: number;
    permiteEngrenagemGrid: boolean;
    paginaAtual:number
    tamanhoPagina: number;
    colunas: IColuna[];
    tamanhoFonte: number
    nomeGrid: string;
    colunaOrdenacao: string
    onAlterarListaSelecionada: (numeroPagina: number) => void;
    onAlterarOrdenacaoDados: (coluna: string, decrescente: boolean) => void;
    onAlterarFonte: (tamanho: number) => void;
    onToggleColunaSelecionada: (nomeColuna: string, selecionada: boolean) => void
    onToggleSelecionarTodasColunas: (selecionar: boolean) => void;
    ordemOrdenacaoDecrescente: boolean
    usaFonteDeReceita?: boolean // 487678/449093
};

export const DocumentosQuitados = React.memo((props: DocumentoQuitadoProps) => {

    const exibirFonteDeReceita = props.usaFonteDeReceita == true;

    const legendaDocumentosQuitados = <section >
        <Legenda itens={[
            {
                nome: "Lucros e Perdas",
                cor: "#00c800"
            },
        ]} />
    </section>

    const dadosClienteDocQuitados =
        <section className={clsx(estilosFormulario.formularioSimplificado, classeParaLabelsFormularioComFundoEscuroSimplificada)}>
            <label>CPF/CNPJ</label><span>{props.cpfCnpj}</span>
            <label>Nome</label><span>{props.nome}</span>
            {<section style={{ marginBottom: "20px" }} />}
        </section>

    const ordenacao = useMemo(() => [
        ...colunasBase,
        ...(exibirFonteDeReceita ? [nomeColunaFonteReceita] : [])
    ], [exibirFonteDeReceita])

    const colunasOrdenadas = useMemo(() => sortBy(
        props.colunas.filter((coluna) => exibirFonteDeReceita || coluna.Nome != nomeColunaFonteReceita),
        (coluna) => indexOf(ordenacao, coluna.Nome)
    ), [props.colunas, exibirFonteDeReceita, ordenacao])

    const colunaFonteReceitaSelecionada = useMemo(() =>
        colunasOrdenadas.some((coluna) => coluna.Nome == nomeColunaFonteReceita && coluna.Selecionada == true),
        [colunasOrdenadas])

    const colunasSelecionadas = useMemo(() => colunasOrdenadas?.filter(c => c.Selecionada), [colunasOrdenadas])

    const corpoTabelaDocQuitado =
        <tbody style={{ fontSize: fonteGradeOuHerdada(props.tamanhoFonte) }}> {
            props.entidades?.map((item) => (
                <tr
                    className={`_table-row ${estiloTabela.dadoTexto} ${estiloTabela.linhaClicavel}`}
                    id={`documentosQuitadosCliente_linha_${item.OidContaReceber}`}
                    key={item.OidContaReceber.toString()}
                >
                    {colunasSelecionadas?.map(coluna => (
                        <CelulaDocumentoQuitado
                            key={coluna.Nome}
                            nomeColuna={coluna.Nome}
                            item={item}
                        />
                    ))}
                </tr>
            ))}
        </tbody>

    const rodapeTabelaDocQuitado =
        <tfoot
            id="documentosQuitadosCliente_rodapeTabela"
        >
            <tr
                id ="documentosQuitadosCliente_rodapeTabela_linha"
            >
                <th style={{ paddingTop: "unset" }} colSpan={10} className={estiloTabela.cabecalhoTexto}>Total</th>
                <th style={{ paddingTop: "unset" }} className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.valorTotalDocumentosQuitados, 2, false)}</th>
                <th style={{ paddingTop: "unset" }} colSpan={colunaFonteReceitaSelecionada ? 3 : 2}></th>
            </tr>
        </tfoot>

    const cabecalhoTabelaDocQuitado =
        <thead
            id="documentosQuitadosCliente_cabecalhoTabela"
        >
            <tr className={estiloTabela.dadoTexto}
                id="documentosQuitadosCliente_cabecalhoTabela_linha"
            >
                {colunasSelecionadas?.map(coluna => (
                    <CabecalhoColunaDocumentoQuitado
                        key={coluna.Nome}
                        nomeColuna={coluna.Nome}
                        colunaOrdenacao={props.colunaOrdenacao}
                        ordemOrdenacaoDecrescente={props.ordemOrdenacaoDecrescente}
                        onAlterarOrdenacaoDados={props.onAlterarOrdenacaoDados}
                    />
                ))}
            </tr>
        </thead>


    const tabelaDocumentosQuitados = <>


        <AcoesHeadDocumentosQuitados
            nomeGrid={props.nomeGrid}
            onToggleColunaSelecionada={props.onToggleColunaSelecionada}
            onAlterarFonte={props.onAlterarFonte}
            tamanhoFonte={props.tamanhoFonte}
            colunas={colunasOrdenadas}
            onToggleSelecionarTodasColunas={props.onToggleSelecionarTodasColunas}
            permiteEngrenagemGrid={props.permiteEngrenagemGrid ?? false} // 418506

        />

        <div className={estiloTabela.containerRolavel}>

            <table className={`_table _w-full _border-collapse text-right ${estiloTabela.tabela}`} id="Cliente_DocumentosQuitados">
                {cabecalhoTabelaDocQuitado}
                {corpoTabelaDocQuitado}
                {rodapeTabelaDocQuitado}
            </table>
        </div>
    </>


    return (
        <div id="DocumentosQuitados">

            {dadosClienteDocQuitados}
            {tabelaDocumentosQuitados}


            {props.numeroTotalPaginas > 1 && (
                <div style={{ marginTop: "0.5em" }}>
                    <Paginacao
                        paginaAtual={props.paginaAtual}
                        totalPaginas={props.numeroTotalPaginas}
                        id="Cliente_DocumentosQuitados_Paginacao"
                        onAlterarListaSelecionada={props.onAlterarListaSelecionada}
                    />
                </div>
            )}

            {legendaDocumentosQuitados}
            {<section style={{ marginBottom: "100px" }} />}
        </div>
    )
})
